/**
 * `rollChoreography` — the overlay's **input contract**. It turns a `RollResult` from
 * `@lancer-vtt/rules` into (a) a flat list of physical dice to throw, each with the face it must
 * land on, and (b) a human-readable summary for the result panel.
 *
 * **The roll authority is `dice.ts`, not this module.** `RollResult.draws` already records
 * *every* die that was rolled and `RollResult.kept` flags which ones contributed to the total
 * (the rest dropped by accuracy keep-highest, crit better-half, etc.). We only *read* that —
 * we never re-roll, re-derive a total, or re-implement notation. {@link diceSpecsFromRoll} is a
 * 1:1 projection of `draws`/`kept`; {@link formatRollSummary} reads the descriptor + recorded
 * total purely for display. Dropped dice are surfaced as **visual treatment** (a dimmed die),
 * never as a separate source of truth — they still appear, they just read as "not counted."
 *
 * Pure + framework-free (no `three`/`cannon-es`/DOM): testable headlessly, and the only Lancer
 * dependency is the `RollResult` *type*. {@link ./DiceStage} consumes {@link DiceSpec}s to
 * choreograph the throw; {@link ./DiceOverlay} renders {@link formatRollSummary} in the panel.
 */
import type { RollResult } from "@lancer-vtt/rules";

/** One physical die to throw: the face it must land on, plus whether it counts toward the total. */
export type DiceSpec = {
  /** Faces on the source die (20/6/3 in Lancer) — selects the mesh via `geometryForSides`. */
  sides: number;
  /** The recorded value this die must land showing (from `RollResult.draws[i].value`). */
  value: number;
  /** Whether this draw was kept (true) or dropped by keep-highest/crit (false → dimmed). */
  kept: boolean;
};

/**
 * Project a `RollResult` onto the dice the overlay throws — one die per recorded draw, in draw
 * order, each carrying its recorded value and kept/dropped flag. This is the whole "what to
 * show" contract: the overlay lands die `i` on `draws[i].value`.
 */
export function diceSpecsFromRoll(result: RollResult): DiceSpec[] {
  return result.draws.map((draw, i) => ({
    sides: draw.sides,
    value: draw.value,
    kept: result.kept[i] ?? true,
  }));
}

/**
 * A terse, display-only summary of a `RollResult`, e.g. `2d6 = 7`, `d20 +5acc = 18`,
 * `4d6 crit = 19`. Reads the descriptor (count/sides/crit), the recorded accuracy modifier,
 * and the recorded total — it does **not** recompute anything. A flat attack/damage bonus
 * (the resolver's `+3`, added outside the dice layer) isn't part of a `RollResult` and so isn't
 * shown here.
 */
export function formatRollSummary(result: RollResult): string {
  const { descriptor, accuracyMod, total } = result;
  const base = descriptor.count === 1 ? `d${descriptor.sides}` : `${descriptor.count}d${descriptor.sides}`;
  const tags: string[] = [];
  if (descriptor.crit) tags.push("crit");
  if (accuracyMod !== undefined && accuracyMod !== 0) {
    const sign = accuracyMod > 0 ? "+" : "−";
    tags.push(`${sign}${Math.abs(accuracyMod)}${accuracyMod > 0 ? "acc" : "dif"}`);
  } else if (descriptor.keep !== undefined && descriptor.keep < descriptor.count) {
    tags.push(`keep ${descriptor.keep}`);
  }
  const label = tags.length > 0 ? `${base} ${tags.join(" ")}` : base;
  return `${label} = ${total}`;
}
